import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Badge from '@mui/material/Badge';

import MailIcon from '@mui/icons-material/Mail';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';

import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import StorefrontIcon from '@mui/icons-material/Storefront';
import StarsIcon from '@mui/icons-material/Stars';
import SettingsIcon from '@mui/icons-material/Settings';
import ArticleIcon from '@mui/icons-material/Article';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import TourIcon from '@mui/icons-material/Tour';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ScienceIcon from '@mui/icons-material/Science';

import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import { useHistory } from "react-router-dom";

import ManageListings from '../Views/ManageListings'
import ManageOperators from '../Views/ManageOperators'
import ManageRequests from '../Views/ManageRequests'
import ManageSupportRequests from '../Views/ManageSupportRequests'
import ManageFeatured from '../Views/ManageFeatured'
import ManageOptions from '../Views/ManageOptions'
import ManageTags from '../Views/ManageTags';
import CMSUpload from '../Views/CMSUpload'
import ManageBlogs from '../Views/ManageBlogs'
import CheckWebsiteUrls from '../Views/CheckWebsiteUrls'
import ManageUsers from '../Views/ManageUsers'
import ManageHospitals from '../Views/ManageHospitals'
import ManagePendingTours from '../Views/ManagePendingTours'
import ManageMedicaidVariables from '../Views/ManageMedicaidVariables'
import ManageMedicaidTests from '../Views/ManageMedicaidTests'
import { AuthContext } from '../../contexts/AuthContext';
import { RequestContext } from '../../contexts/RequestContext';
import { UserContext } from '../../contexts/UserContext';


// Temporarily hidden from the drawer. The views still exist; remove a key here to restore it.
const HIDDEN_VIEWS = ['featured', 'requests', 'hospital', 'pendingTours'];

const drawerWidth = 240;

function ResponsiveDrawer(props) {
  const { window } = props;
  const history = useHistory();
  const { logout, user } = useContext(AuthContext);
  const { unviewedRequests } = useContext(RequestContext);
  const { unviewedUsers } = useContext(UserContext);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedView, setSelectedView] = useState('listing');

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <div>
      <Toolbar />
      <Divider />
      <List>
        {[
          { text: 'Manage Listings', component: 'listing', icon: <MapsHomeWorkIcon /> },
          { text: 'Manage Operator', component: 'operator', icon: <StorefrontIcon /> },
          { text: 'Manage Featured', component: 'featured', icon: <StarsIcon /> },
          { text: 'Manage Options', component: 'options', icon: <SettingsIcon /> },
          { text: 'Manage Blogs', component: 'blog', icon: <ArticleIcon /> },
          { text: 'Upload CMS Data', component: 'cmsUpload', icon: <CloudUploadIcon /> },
          { text: 'Check Website Urls', component: 'websiteCheck', icon: <LinkIcon /> },
          { text: 'Manage Tags', component: 'tags', icon: <LocalOfferIcon /> },
          {
            text: 'Manage Users',
            component: 'users',
            icon: (
              <Badge
                badgeContent={unviewedUsers.length}
                color="error"
                invisible={unviewedUsers.length === 0}
              >
                <PeopleIcon />
              </Badge>
            )
          },
          {
            text: 'Manage Requests',
            component: 'requests',
            icon: (
              <Badge
                badgeContent={unviewedRequests.length}
                color="error"
                invisible={unviewedRequests.length === 0}
              >
                <AssignmentIcon />
              </Badge>
            )
          },
          { text: 'Support Requests', component: 'supportRequests', icon: <SupportAgentIcon /> },
          { text: 'Manage Hospitals', component: 'hospital', icon: <LocalHospitalIcon /> },
          { text: 'Pending Tours', component: 'pendingTours', icon: <TourIcon /> },
          { text: 'Manage AI Context', component: 'medicaidVariables', icon: <PsychologyIcon /> },
          { text: 'Medicaid Tests', component: 'medicaidTests', icon: <ScienceIcon /> }
        ].filter((data) => !HIDDEN_VIEWS.includes(data.component)).map((data) => (
          <ListItem button key={data.text} onClick={() => { setSelectedView(data.component) }}>
            <ListItemIcon>
              {data.icon}
            </ListItemIcon>
            <ListItemText primary={data.text} />
          </ListItem>
        ))}
      </List>
    </div>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Manage Onsite Content
          </Typography>
          {user && (
            <>
              {/* Optionally show user info here */}
              <Button color="inherit" onClick={() => { logout(); history.push('/'); }}>
                Logout
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar />
        {selectedView === 'listing' ?
          <ManageListings />
          : selectedView === 'operator' ?
            <ManageOperators />
            : selectedView === 'requests' ?
              <ManageRequests />
              : selectedView === 'supportRequests' ?
              <ManageSupportRequests />
              : selectedView === 'options' ?
              <ManageOptions />
              : selectedView === 'cmsUpload' ?
              <CMSUpload />
              : selectedView === 'blog' ?
               <ManageBlogs />
               : selectedView === 'websiteCheck' ?
                <CheckWebsiteUrls />
                : selectedView === 'tags' ?
                <ManageTags />
                : selectedView === 'users' ?
                <ManageUsers />
                : selectedView === 'hospital' ?
                 <ManageHospitals />
                : selectedView === 'pendingTours' ?
                 <ManagePendingTours />
                : selectedView === 'medicaidVariables' ?
                 <ManageMedicaidVariables />
                : selectedView === 'medicaidTests' ?
                 <ManageMedicaidTests />
                : selectedView === 'featured' ?
                 <ManageFeatured />
              :
              <ManageListings />
        }

      </Box>
    </Box>
  );
}

ResponsiveDrawer.propTypes = {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window: PropTypes.func,
};

export default ResponsiveDrawer;